import { useMediaQuery } from '@uhg-abyss/web/hooks/useMediaQuery';
import { config } from '@uhg-abyss/web/tools/config';
import { useSearchParams } from '@uhg-abyss/web/tools/reactRouterTools';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Popover } from '@uhg-abyss/web/ui/Popover';
import { t } from 'i18next';
import React, { useContext, useEffect } from 'react';

import { Benefits } from '../../../../../api/src/models/ProviderRecommendationDetails';
import { adobeLinkTrackEvent } from '../../../common/AdobeTagging/adobeLinkTrackEvent';
import { ReverseCoverageTypesCodes } from '../../../common/Constants';
import { ConstantsLagoon } from '../../../common/ConstantsLagoon';
import { PSXIcon } from '../../../common/PSXIcon';
import { isUmrMember } from '../../../common/Utils/memberUtils/memberUtils';
import { PortalContext } from '../../../context/PortalContext';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { useLagoon } from '../../../hooks/useLagoon';
import { useLagoonMessage } from '../../../hooks/useLagoonMessage';
import { useSmartChoice } from '../../../hooks/useSmartChoice';
import { StoreKeys } from '../../../hooks/useStore/state';
import { useStore } from '../../../hooks/useStore/useStore';
import {
  ProviderDetails,
  ProviderLocation,
} from '../../../models/ProviderDetails';
import {
  RecommendationCriteriaMatch,
  buildFaqUrl,
  countAllMeasures,
  getMatchCount,
  getMatchDescription,
  getMatchText,
} from '../../../utils/smartChoice.utils';
import {
  getCurrentMember,
  getMarketType,
  getProductCodeForCoverageType,
} from '../../../utils/user.utils';
import { adobeModalTrackEvent } from '../../AdobeTagging/adobeModalTrackEvent';
import { phoneOnly } from '../../ConstantsStyles';
import {
  defaultStringToEmpty,
  getAcoBenefitPlan,
} from '../../Widget/SmartChoiceWidget/utils';
import { MatchText } from '../MatchText';
import { NotAMatchText } from '../NotAMatchText';
import {
  BreakdownContainer,
  CriteriaDescription,
  CriteriaPointsContainer,
  CriteriaSubTitle,
  CriteriaTitle,
  DescriptionContainer,
  DrawerContentWithBackgroundgray,
  FAQContainerWithOutTopBorder,
  FAQLink,
  StyledDescription,
} from '../SmartChoiceBreakDown.styles';
import {
  TextOnTopOfProgressBar,
  TotalCostOfCareContainer,
} from './BenfitsBreakDown.styles';
import { MatchTextWithTierLocations } from './Components/MatchTextWithTierLocations';
import { NotMatchTextWithTierLocations } from './Components/NotMatchTextWithTierLocations';
import TotalCostOfCareProgressBar from './Components/TotalCostOfCareProgressBar';
import { TotalCostOfCareTextWithLink } from './Components/TotalCostOfCareTextWithLink';
import {
  Maybe,
  acoProviderLocations,
  colorAndText,
  defaultBooleanToFalse,
  tieredProviderLocations,
} from './Components/utils';

interface BenifitsBreakDownProps {
  benefits: Benefits;
  acoName: string;
  bandedCost?: string;
  providerDetails: ProviderDetails;
  selectedLocatoionCity: string;
  providerLocationData: ProviderLocation;
  handleTabChange: (tab: string | number) => void;
  isCareAvl?: boolean;
  isTieredPlan?: boolean;
  chipBackgroundColor?: string;
}

const BANDED_COST_TO_INDICATOR = {
  below: '$',
  meets: '$$',
  above: '$$$',
} as const;

const getIsCostIndicator = (
  tired: boolean,
  aco: boolean,
  indicator: Maybe<string>,
  isWidget?: boolean
): boolean =>
  (isWidget || (!tired && !aco)) &&
  (indicator === '$' ||
    indicator === '$$' ||
    indicator === '$$$' ||
    !indicator ||
    indicator?.toLocaleLowerCase() === 'n/a');

const getTieredCount = (
  isTieredPlan: boolean,
  isProviderTierDesignation: string,
  isAcoBenefitPlan: boolean,
  isAcoParticipationStatus: boolean
): number => {
  let count = 0;
  if (
    isTieredPlan &&
    (isProviderTierDesignation === 'y' || isProviderTierDesignation === 'n')
  ) {
    count += 1;
  }
  if (
    isAcoBenefitPlan &&
    (isAcoParticipationStatus || !isAcoParticipationStatus)
  ) {
    count += 1;
  }
  return count;
};

const replacePlaceholders = (
  text: string | undefined,
  replacements: Record<string, string>
): string =>
  (text || '').replace(/\{\{(.*?)}}/g, (_, key) => {
    const keys = key.split('.');
    let value: any = replacements;
    for (const k of keys) {
      value = value?.[k];
      if (value === undefined) break;
    }
    return typeof value === 'string' ? value : '';
  });

const getHasAcoParticipation = (
  isAcoParticipationStatus: boolean,
  acoProviderLength: number
): boolean => isAcoParticipationStatus && acoProviderLength > 0;

const getShowCostViewLink = (
  isWidget: boolean,
  enableViewCostLink: boolean
): boolean => !isWidget && enableViewCostLink;

const getShowCareViewLink = (
  showCostViewLink: boolean,
  isCareAvl: boolean
): boolean => showCostViewLink && isCareAvl;

const getCostEfficiencyRatingNode = (
  rating: string,
  isCostEfficiencyRating: boolean,
  showCareViewLink: boolean,
  handleTabChange: (tab: string | number) => void,
  belowMsg: string | undefined,
  averageMsg: string | undefined,
  aboveMsg: string | undefined
): React.ReactElement | null => {
  switch (rating) {
    case 'A':
    case 'B':
    case 'C':
    case 'D':
      return isCostEfficiencyRating ? (
        <MatchTextWithTierLocations
          handleTabChange={handleTabChange}
          isCost
          isViewLink={showCareViewLink}
          text={belowMsg ?? ''}
        />
      ) : null;
    case 'E':
      return (
        <MatchTextWithTierLocations
          handleTabChange={handleTabChange}
          isCost
          isViewLink={showCareViewLink}
          text={averageMsg ?? ''}
        />
      );
    case 'F':
    case 'G':
      return (
        <NotMatchTextWithTierLocations
          handleTabChange={handleTabChange}
          isCost
          isViewLink={showCareViewLink}
          text={aboveMsg ?? ''}
        />
      );
    default:
      return null;
  }
};

const getCostIndicatorNode = (
  indicator: Maybe<string>,
  belowMsg: string | undefined,
  averageMsg: string | undefined,
  aboveMsg: string | undefined,
  noCostInformationAvailableMsg: string | undefined,
  isWidget?: boolean
): React.ReactElement | null => {
  if (indicator === '$') return <MatchText text={belowMsg ?? ''} />;
  if (indicator === '$$') return <MatchText text={averageMsg ?? ''} />;
  if (indicator === '$$$') return <NotAMatchText text={aboveMsg ?? ''} />;
  if (isWidget && (!indicator || indicator?.toLocaleLowerCase() === 'n/a'))
    return <NotAMatchText text={noCostInformationAvailableMsg ?? ''} />;
  return null;
};

const getTieringLevelNode = (
  tieringLevel: string,
  tierL1Msg: string | undefined,
  tierL2Msg: string | undefined,
  tierL3Msg: string | undefined
): React.ReactElement | null => {
  const tierIcon = <PSXIcon icon="Group 204976975" size={24} />;
  // Handle both "1" and "L1" formats
  if (tieringLevel === '1' || tieringLevel === 'L1') {
    return <MatchText StartIcon={tierIcon} text={tierL1Msg ?? ''} />;
  }
  if (tieringLevel === '2' || tieringLevel === 'L2') {
    return <MatchText StartIcon={tierIcon} text={tierL2Msg ?? ''} />;
  }
  if (tieringLevel === '3' || tieringLevel === 'L3') {
    return <MatchText StartIcon={tierIcon} text={tierL3Msg ?? ''} />;
  }
  return null;
};

const getPreferredLabNetworkNode = (
  indicator: string,
  yesMsg: string | undefined,
  noMsg: string | undefined
): React.ReactElement | null => {
  if (indicator === 'Y') return <MatchText text={yesMsg ?? ''} />;
  if (indicator === 'N') return <NotAMatchText text={noMsg ?? ''} />;
  return null;
};

const getOxfordPreferredLabNetworkNode = (
  indicator: string,
  yesMsg: string | undefined,
  noMsg: string | undefined
): React.ReactElement | null => {
  if (indicator === 'Y') return <MatchText text={yesMsg ?? ''} />;
  if (indicator === 'N') return <NotAMatchText text={noMsg ?? ''} />;
  return null;
};

const getDesignatedDiagnosticLabNode = (
  indicator: string,
  yesMsg: string | undefined,
  noMsg: string | undefined
): React.ReactElement | null => {
  if (indicator === 'Y') return <MatchText text={yesMsg ?? ''} />;
  if (indicator === 'N') return <NotAMatchText text={noMsg ?? yesMsg ?? ''} />;
  return null;
};

const getFreeStandingFacilityNode = (
  indicator: string,
  yesMsg: string | undefined,
  noMsg: string | undefined
): React.ReactElement | null => {
  if (indicator === 'Y') return <MatchText text={yesMsg ?? ''} />;
  if (indicator === 'N') return <NotAMatchText text={noMsg ?? ''} />;
  return null;
};

const renderTieredContent = (
  isProviderTierDesignation: Maybe<String>,
  tieredProviderLocation: string[],
  isWidget: boolean,
  selectedLocatoionCity: string,
  handleTabChange: (tab: string | number) => void,
  preferred_tier1_and_loactions: { message?: string } | undefined,
  not_prefered_tier1_and_loactions: { message?: string } | undefined,
  provider_tier: { message?: string } | undefined
): React.ReactElement | null => {
  if (isProviderTierDesignation === 'y') {
    if (tieredProviderLocation.length > 1) {
      return (
        <MatchTextWithTierLocations
          StartIcon={<PSXIcon icon="Group 204976975" size={24} />}
          handleTabChange={handleTabChange}
          isViewLink={!isWidget}
          text={replacePlaceholders(preferred_tier1_and_loactions?.message, {
            selectedLocatoionCity,
          })}
        />
      );
    }
    return (
      <MatchText
        StartIcon={<PSXIcon icon="Group 204976975" size={24} />}
        text={provider_tier?.message ?? ''}
      />
    );
  }
  if (isProviderTierDesignation === 'n') {
    if (tieredProviderLocation.length >= 1) {
      return (
        <NotMatchTextWithTierLocations
          handleTabChange={handleTabChange}
          isViewLink={!isWidget}
          text={replacePlaceholders(not_prefered_tier1_and_loactions?.message, {
            selectedLocatoionCity,
          })}
        />
      );
    }
    return <NotAMatchText text={provider_tier?.message ?? ''} />;
  }
  return null;
};

export const BenifitsBreakDown = ({
  benefits,
  acoName,
  bandedCost,
  providerDetails,
  providerLocationData,
  selectedLocatoionCity,
  handleTabChange,
  isCareAvl,
  isTieredPlan: widgetIsTieredPlan,
  chipBackgroundColor,
}: BenifitsBreakDownProps) => {
  const { portalData } = useContext(PortalContext);
  const [searchParams] = useSearchParams();
  const isWidget = useStore(StoreKeys?.IS_WIDGET);
  const isMobile = useMediaQuery(phoneOnly);

  const selectedMember = getCurrentMember();
  const isUmr = isUmrMember(portalData?.portalName);
  const baseUrl = isUmr ? config('UMR_BASE_URL') : config('BASE_URL');
  const isTieredPlanSearchParam = searchParams.get('isTieredPlan');
  const providerData = providerDetails;
  const providerLocations = providerData?.providerLocations;

  const featureSuppression = useSmartChoice();
  const isTieredPlan = isWidget
    ? widgetIsTieredPlan
    : isTieredPlanSearchParam === 'true' && featureSuppression;
  const tieredProviderLocation = tieredProviderLocations(providerLocations);

  const acoProvider = providerLocationData?.acoProvider ?? [];
  const acoProviderLocation = acoProviderLocations(providerLocations);
  const normalizedBandedCost = bandedCost?.trim().toLowerCase();
  const providerCostIndicator = isWidget
    ? BANDED_COST_TO_INDICATOR[
        normalizedBandedCost as keyof typeof BANDED_COST_TO_INDICATOR
      ]
    : benefits?.costIndicator;
  const providerCostEfficiencyCode = providerLocationData?.superPremium ?? '';
  const isPreferredProviderStatus: boolean =
    providerLocationData?.isGreenDiamondProvider ?? false;
  const isProviderTierDesignation: Maybe<String> =
    benefits?.providerTierDesignation?.toLowerCase();
  const providerTieringLevel = benefits?.providerTieringLevel ?? '';
  const preferredLabNetworkIndicator =
    benefits?.preferredLabNetworkIndicator ?? '';
  const oxfordPreferredLabNetworkIndicator =
    benefits?.oxfordPreferredLabNetworkIndicator ?? '';
  const designatedDiagnosticLab = benefits?.designatedDiagnosticLab ?? '';
  const freeStandingFacilityIndicator =
    benefits?.freeStandingFacilityIndicator ?? '';

  const isAcoParticipationStatus: boolean =
    benefits?.acoParticipationStatus === 'Y';
  const isACOProvider =
    acoProvider?.length > 0 && !defaultBooleanToFalse(featureSuppression);
  const isCostIndicator = getIsCostIndicator(
    isTieredPlan,
    isACOProvider,
    providerCostIndicator,
    isWidget
  );

  useEffect(() => {
    adobeModalTrackEvent('Smart choice benefits breakdown');
  }, []);

  const allConfigs = useLagoon('config')();
  const [enableViewCostLink, enableTotalCostOfCareProgressBar] = useFeatureFlag(
    [
      ConstantsLagoon.FEATURE_FLAGS.ENABLE_VIEW_COST_LINK,
      ConstantsLagoon.FEATURE_FLAGS.ENABLE_TOTAL_COST_OF_CARE_PROGRESS_BAR,
    ]
  );
  const isCostEfficiencyRating =
    providerCostEfficiencyCode !== '' &&
    benefits?.costEfficiencyRating !== 'NA';
  const isCostEfficiencyCountAvl =
    isCostEfficiencyRating &&
    benefits?.costEfficiencyRating !== 'F' &&
    benefits?.costEfficiencyRating !== 'G' &&
    !isWidget;
  const acoBenefitPlanCollection = allConfigs?.find(
    (row) => row.key === 'ACO_BENEFIT_COLLECTION'
  )?.value;
  const isAcoBenefitPlan = getAcoBenefitPlan(
    defaultStringToEmpty(
      getProductCodeForCoverageType(
        selectedMember,
        ReverseCoverageTypesCodes.MEDICAL
      )
    ),
    defaultStringToEmpty(getMarketType(selectedMember)),
    acoBenefitPlanCollection
  );

  const inNetworkStatus = benefits?.inNetworkStatus !== 'N';
  const hasTierDesignation =
    isProviderTierDesignation === 'y' || isProviderTierDesignation === 'n';
  const count = getMatchCount(
    isPreferredProviderStatus,
    isTieredPlan && hasTierDesignation,
    isAcoParticipationStatus,
    isCostIndicator,
    isCostEfficiencyCountAvl,
    inNetworkStatus
  );

  const tieredCountValue = isPreferredProviderStatus
    ? 1
    : getTieredCount(
        isTieredPlan,
        isProviderTierDesignation,
        isAcoBenefitPlan,
        isAcoParticipationStatus
      );
  const totalCount = countAllMeasures(
    tieredCountValue,
    isCostIndicator,
    inNetworkStatus,
    isCostEfficiencyRating && !isWidget
  );

  const chipValueFromParams = searchParams.get('chipValue');
  const chipValue = chipValueFromParams ? chipValueFromParams : '';

  // Fetch common FAQ message without suffix
  const allUiMessages = useLagoon('ui-messaging')();
  const FAQs = allUiMessages?.find(
    (msg: any) =>
      msg.key === ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_BREAKDOWN_FAQ_TEXT
  );

  const getMessage = useLagoonMessage({
    chipValue,
    providerType: providerDetails?.providerType || '',
  });

  const [
    preferredLocationSubtitle,
    description,
    in_network_provider,
    out_network_provider,
    preferred_provider,
    provider_tier,
    provider_tier_l1,
    provider_tier_l2,
    provider_tier_l3,
    preferred_tier1_and_loactions,
    not_prefered_tier1_and_loactions,
    provider_participated_aco,
    provider_participates_aco_name_and_location,
    provider_does_not_participate_aco,
    provider_does_not_participate_with_location,
    costSubtitle,
    below_average_cost_indicator,
    average_cost_indicator,
    above_average_cost_indicator,
    no_cost_information_available,
    below_average_cost_of_care,
    average_cost_of_care,
    above_average_cosst_of_care,
    preferred_lab_network_msg,
    not_preferred_lab_network_msg,
    oxford_preferred_lab_network_msg,
    not_oxford_preferred_lab_network_msg,
    designated_diagnostic_lab_msg,
    not_designated_diagnostic_lab_msg,
    free_standing_facility_msg,
    not_free_standing_facility_msg,
    all_criteria_met,
    totalCostOfCare,
    totalCostOfCareInfoTitleAndContent,
    totalCostOfCareProgressBarTopLeftText,
    totalCostOfCareProgressBarTopRightText,
    totalCostOfCareBelowAverageCostText,
    totalCostOfCareAverageCostText,
    totalCostOfCareAboveAverageCostText,
  ] = getMessage([
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_BENEFITS_BREAKDOWN
      .BENIFITS_BREAKDOWN_PREFERRED_PROVIDER_AND_LOCATION_SUB_TITLE,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_BENEFITS_BREAKDOWN
      .BENIFITS_BREAKDOWN_DESCRIPTION,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_BENEFITS_BREAKDOWN
      .BENIFITS_BREAKDOWN_IN_NETWORK,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_BENEFITS_BREAKDOWN
      .BENIFITS_BREAKDOWN_OUT_NETWORK,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_BENEFITS_BREAKDOWN
      .BENIFITS_BREAKDOWN_PREFERRED_PROVIDER,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_BENEFITS_BREAKDOWN
      .BENIFITS_BREAKDOWN_PROVIDER_TIER,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_BENEFITS_BREAKDOWN
      .BENIFITS_BREAKDOWN_TIER_L1,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_BENEFITS_BREAKDOWN
      .BENIFITS_BREAKDOWN_TIER_L2,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_BENEFITS_BREAKDOWN
      .BENIFITS_BREAKDOWN_TIER_L3,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_BENEFITS_BREAKDOWN
      .BENIFITS_BREAKDOWN_PROVIDER_TIER1_WITH_LOCATION,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_BENEFITS_BREAKDOWN
      .BENIFITS_BREAKDOWN_PROVIDER_ISNOT_TIER1_WITH_LOCATION,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_BENEFITS_BREAKDOWN
      .BENIFITS_BREAKDOWN_PROVIDER_PARTICIPATES_ACO_NAME,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_BENEFITS_BREAKDOWN
      .BENIFITS_BREAKDOWN_PROVIDER_PRATICIPATES_ACO_NAME_AND_LOCATION,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_BENEFITS_BREAKDOWN
      .BENIFITS_BREAKDOWN_DOES_NOT_PARTICIPATES_ELIGIBLE_AT_ACO_NAME,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_BENEFITS_BREAKDOWN
      .BENIFITS_BREAKDOWN_PROVIDER_NOT_PARTICIPATES_ACO_NAME_AND_LOCATION,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_BENEFITS_BREAKDOWN
      .BENIFITS_BREAKDOWN_COST_SUB_TITLE,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_BENEFITS_BREAKDOWN
      .BENIFITS_BREAKDOWN_BELOW_AVARAGE_COST_FOR_OFFICE_VISIT,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_BENEFITS_BREAKDOWN
      .BENIFITS_BREAKDOWN_AVERAGE_COST_FOR_OFFICE_VISIT,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_BENEFITS_BREAKDOWN
      .BENIFITS_BREAKDOWN_ABOVE_AVERAGE_COST_FOR_OFFICE_VISIT,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_BENEFITS_BREAKDOWN
      .BENIFITS_BREAKDOWN_NO_COST_INFORMATION_AVAILABLE,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_BENEFITS_BREAKDOWN
      .BENIFITS_BREAKDOWN_BELOW_AVARAGE_TOTAL_COST_OF_CARE,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_BENEFITS_BREAKDOWN
      .BENIFITS_BREAKDOWN_AVERAGE_TOTAL_COST_OF_CARE,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_BENEFITS_BREAKDOWN
      .BENIFITS_BREAKDOWN_ABOVE_AVERAGE_TOTAL_COST_OF_CARE,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_BENEFITS_BREAKDOWN
      .BENIFITS_BREAKDOWN_PREFERRED_LAB_NETWORK,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_BENEFITS_BREAKDOWN
      .BENIFITS_BREAKDOWN_NOT_PREFERRED_LAB_NETWORK,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_BENEFITS_BREAKDOWN
      .BENIFITS_BREAKDOWN_OXFORD_PREFERRED_LAB_NETWORK,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_BENEFITS_BREAKDOWN
      .BENIFITS_BREAKDOWN_NOT_OXFORD_PREFERRED_LAB_NETWORK,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_BENEFITS_BREAKDOWN
      .BENIFITS_BREAKDOWN_DESIGNATED_DIAGNOSTIC_LAB,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_BENEFITS_BREAKDOWN
      .BENIFITS_BREAKDOWN_NOT_DESIGNATED_DIAGNOSTIC_LAB,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_BENEFITS_BREAKDOWN
      .BENIFITS_BREAKDOWN_FREE_STANDING,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_BENEFITS_BREAKDOWN
      .BENIFITS_BREAKDOWN_NOT_FREE_STANDING,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_QUALITY_BREAKDOWN
      .QUALITY_BREAKDOWN_ALL_CRITERIA_MET_TITLE_AND_DESCRIPTION,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_BENEFITS_BREAKDOWN
      .BENEFIT_TOTAL_COST_OF_CARE_TEXT,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_BENEFITS_BREAKDOWN
      .BENEFIT_TOTAL_COST_OF_CARE_INFO_TITLE_AND_CONTENT,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_BENEFITS_BREAKDOWN
      .BENEFIT_TOTAL_COST_OF_CARE_PROGRESS_BAR_TOP_LEFT_TEXT,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_BENEFITS_BREAKDOWN
      .BENEFIT_TOTAL_COST_OF_CARE_PROGRESS_BAR_TOP_RIGHT_TEXT,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_BENEFITS_BREAKDOWN
      .BENEFIT_TOTAL_COST_OF_CARE_BELOW_AVERAGE_COST_TEXT,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_BENEFITS_BREAKDOWN
      .BENEFIT_TOTAL_COST_OF_CARE_AVERAGE_COST_TEXT,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_BENEFITS_BREAKDOWN
      .BENEFIT_TOTAL_COST_OF_CARE_ABOVE_AVERAGE_COST_TEXT,
  ]);

  const outOf = t('OUT_OF');
  const outOfTotal = getMatchText(
    benefits?.scoreDetails?.match as string,
    `(${count} ${outOf} ${totalCount})`
  );
  const hasAcoParticipationWithProvider = getHasAcoParticipation(
    isAcoParticipationStatus,
    acoProvider?.length ?? 0
  );
  const showCostViewLink = getShowCostViewLink(
    Boolean(isWidget),
    Boolean(enableViewCostLink)
  );
  const showCareViewLink = getShowCareViewLink(
    showCostViewLink,
    Boolean(isCareAvl)
  );
  const faqUrl = buildFaqUrl(baseUrl, isWidget);

  const showCostEfficiencySection =
    !!benefits?.costEfficiencyRating && benefits?.costEfficiencyRating !== 'NA';
  const costRatingData = colorAndText(benefits?.costEfficiencyRating ?? '');
  const costTextMap = {
    belowAverage: totalCostOfCareBelowAverageCostText?.message,
    average: totalCostOfCareAverageCostText?.message,
    aboveAverage: totalCostOfCareAboveAverageCostText?.message,
  };
  const costText =
    costTextMap[costRatingData?.text as keyof typeof costTextMap] ?? '';

  return (
    <BreakdownContainer data-testid="benefits-breakdown">
      <DrawerContentWithBackgroundgray
        css={
          chipBackgroundColor && enableTotalCostOfCareProgressBar
            ? { backgroundColor: chipBackgroundColor }
            : {}
        }
        direction="column"
      >
        <CriteriaTitle data-testid="match-description">
          {`${t(
            getMatchDescription(
              benefits?.scoreDetails?.match ?? RecommendationCriteriaMatch.None
            )
          )} ${outOfTotal}`}
        </CriteriaTitle>
        <CriteriaDescription data-testid="criteria-met-description">
          {all_criteria_met?.message}
        </CriteriaDescription>
      </DrawerContentWithBackgroundgray>
      <DescriptionContainer data-testid="benefits-breakdown-description">
        <StyledDescription>{description?.message}</StyledDescription>
      </DescriptionContainer>
      <CriteriaPointsContainer
        data-testid="benefits-breakdown-criteria"
        direction="column"
      >
        {showCostViewLink && (
          <CriteriaSubTitle>
            {preferredLocationSubtitle?.message}
          </CriteriaSubTitle>
        )}
        {inNetworkStatus ? (
          <MatchText text={in_network_provider?.message} />
        ) : (
          <NotAMatchText text={out_network_provider?.message ?? ''} />
        )}
        {isPreferredProviderStatus ? (
          <MatchText
            StartIcon={<PSXIcon icon={'green_diamond'} size={24} />}
            text={preferred_provider?.message}
          />
        ) : (
          <>
            {isTieredPlan &&
              renderTieredContent(
                isProviderTierDesignation,
                tieredProviderLocation,
                Boolean(isWidget),
                selectedLocatoionCity,
                handleTabChange,
                preferred_tier1_and_loactions,
                not_prefered_tier1_and_loactions,
                provider_tier
              )}
            {isAcoBenefitPlan && (
              <>
                {hasAcoParticipationWithProvider ? (
                  acoProviderLocation.length > 1 ? (
                    <MatchText
                      text={replacePlaceholders(
                        provider_participates_aco_name_and_location?.message,
                        { selectedLocatoionCity, acoName }
                      )}
                    />
                  ) : (
                    <MatchText
                      text={replacePlaceholders(
                        provider_participated_aco?.message,
                        {
                          acoName,
                        }
                      )}
                    />
                  )
                ) : (
                  !isAcoParticipationStatus && (
                    <>
                      {acoProviderLocation?.length > 1 ? (
                        <NotAMatchText
                          text={replacePlaceholders(
                            provider_does_not_participate_with_location?.message,
                            { selectedLocatoionCity }
                          )}
                        />
                      ) : (
                        <NotAMatchText
                          text={provider_does_not_participate_aco?.message}
                        />
                      )}
                    </>
                  )
                )}
              </>
            )}
          </>
        )}
        {providerTieringLevel &&
          getTieringLevelNode(
            providerTieringLevel,
            provider_tier_l1?.message,
            provider_tier_l2?.message,
            provider_tier_l3?.message
          )}
        {preferredLabNetworkIndicator &&
          getPreferredLabNetworkNode(
            preferredLabNetworkIndicator,
            preferred_lab_network_msg?.message,
            not_preferred_lab_network_msg?.message
          )}
        {oxfordPreferredLabNetworkIndicator &&
          getOxfordPreferredLabNetworkNode(
            oxfordPreferredLabNetworkIndicator,
            oxford_preferred_lab_network_msg?.message,
            not_oxford_preferred_lab_network_msg?.message
          )}
        {designatedDiagnosticLab &&
          getDesignatedDiagnosticLabNode(
            designatedDiagnosticLab,
            designated_diagnostic_lab_msg?.message,
            not_designated_diagnostic_lab_msg?.message
          )}
        {freeStandingFacilityIndicator &&
          getFreeStandingFacilityNode(
            freeStandingFacilityIndicator,
            free_standing_facility_msg?.message,
            not_free_standing_facility_msg?.message
          )}
        {showCostViewLink && (
          <CriteriaSubTitle>{costSubtitle?.message}</CriteriaSubTitle>
        )}
        {isCostIndicator &&
          getCostIndicatorNode(
            providerCostIndicator,
            below_average_cost_indicator?.message,
            average_cost_indicator?.message,
            above_average_cost_indicator?.message,
            no_cost_information_available?.message,
            Boolean(isWidget)
          )}
        {!enableTotalCostOfCareProgressBar &&
          showCostEfficiencySection &&
          getCostEfficiencyRatingNode(
            benefits.costEfficiencyRating!,
            isCostEfficiencyRating,
            showCareViewLink,
            handleTabChange,
            below_average_cost_of_care?.message,
            average_cost_of_care?.message,
            above_average_cosst_of_care?.message
          )}
        {enableTotalCostOfCareProgressBar && showCostEfficiencySection && (
          <>
            <Flex alignItems="center" direction="row">
              {benefits?.costEfficiencyRating === 'F' ||
              benefits?.costEfficiencyRating === 'G' ? (
                <NotAMatchText text={totalCostOfCare?.message} />
              ) : (
                <MatchText text={totalCostOfCare?.message} />
              )}
              <Popover
                content={totalCostOfCareInfoTitleAndContent?.message}
                title={totalCostOfCareInfoTitleAndContent?.title}
                width={isMobile ? '344px' : '242px'}
              >
                <span>
                  <IconSymbol
                    color={'#196ECF'}
                    icon="info"
                    size={20}
                    style={{ paddingLeft: '4px', cursor: 'pointer' }}
                    variant="outlined"
                  />
                </span>
              </Popover>
            </Flex>
            <TotalCostOfCareContainer>
              <Flex justify="space-between">
                <TextOnTopOfProgressBar>
                  {totalCostOfCareProgressBarTopLeftText?.title}
                </TextOnTopOfProgressBar>
                <TextOnTopOfProgressBar>
                  {totalCostOfCareProgressBarTopRightText?.title}
                </TextOnTopOfProgressBar>
              </Flex>
              <TotalCostOfCareProgressBar
                status={benefits?.costEfficiencyRating ?? ''}
              />
              <TotalCostOfCareTextWithLink
                handleTabChange={handleTabChange}
                isCost
                isViewLink={showCareViewLink}
                text={costText}
                textColor={costRatingData?.color}
              />
            </TotalCostOfCareContainer>
          </>
        )}
        {/* show view cost link if cost efficiency value = NA */}
        {!showCostEfficiencySection && showCareViewLink && (
          <TotalCostOfCareTextWithLink
            handleTabChange={handleTabChange}
            isCost
            isViewLink={showCareViewLink}
            text=""
          />
        )}
      </CriteriaPointsContainer>

      <FAQContainerWithOutTopBorder>
        <FAQLink
          data-testid="faq-link"
          href={faqUrl}
          onClick={() => {
            adobeLinkTrackEvent({
              name: 'Faqs',
              location: 'Smart choice benefits breakdown',
            });
          }}
        >
          {FAQs?.message}
        </FAQLink>
      </FAQContainerWithOutTopBorder>
    </BreakdownContainer>
  );
};
